import { StyledTable, TableWrapper } from 'components/data_table/styled';
import { TableBody } from 'components/data_table/TableBody';
import { TableFooter } from 'components/data_table/TableFooter';
import { TableHeader } from 'components/data_table/TableHeader';
import { DataAndKey, TableColumn, TableProps } from 'components/data_table/types';
import { PagingBar } from 'components/paging/PagingBar';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { useRefList } from 'custom_hooks/useRefList';
import { isViewportAtMostMedium, useWindowSize } from 'custom_hooks/useWindowSize';
import { CSSProperties, memo, RefObject, useEffect, useMemo, useState } from 'react';

export type CellLockStyles = Readonly<{
    /**
     * Styles for headers cells.
     */
    headerStyles: CSSProperties[];
    /**
     * Styles for data cells.
     */
    dataStyles: CSSProperties[];
    /**
     * Styles for footer cells.
     */
    footerStyles: CSSProperties[];
}>;

/**
 * Determines the CSS styles needed for locked/unlocked header, data, and footer cells.
 *
 * @param columns The column configuration.
 * @param headerRefs The refs to the actual `<th>` column elements.
 * @param lockHeaders When `true` result will include styles to lock the header row.
 * @param lockFooters When `true` result will include styles to lock the footer row.
 */
export function getCellLockStyles<TData>(
    columns: TableColumn<TData>[],
    headerRefs: RefObject<HTMLTableHeaderCellElement>[],
    lockHeaders: boolean,
    lockFooters: boolean
): CellLockStyles {
    const colInfo = columns.map(({ locked }, index) => ({
        locked,
        index,
        width: headerRefs[index].current?.offsetWidth ?? 0,
    }));

    const commonStyles = colInfo.map<CSSProperties>(({ locked }, colIndex) => ({
        left: locked
            ? colInfo
                  .filter(({ locked: isLocked, index }) => index < colIndex && isLocked)
                  .reduce((sum, { width }) => sum + width, 0)
            : undefined,
        right: locked
            ? colInfo
                  .filter(({ locked: isLocked, index }) => index > colIndex && isLocked)
                  .reduce((sum, { width }) => sum + width, 0)
            : undefined,
    }));

    return {
        headerStyles: columns.map<CSSProperties>(({ locked }, colIndex) => ({
            ...commonStyles[colIndex],
            top: lockHeaders ? 0 : undefined,
            position: locked || lockHeaders ? 'sticky' : 'relative',
            zIndex: locked ? 2 : 1,
        })),
        dataStyles: columns.map<CSSProperties>(({ locked }, colIndex) => ({
            ...commonStyles[colIndex],
            position: locked ? 'sticky' : 'relative',
            zIndex: locked ? 1 : 0,
        })),
        footerStyles: columns.map<CSSProperties>(({ locked }, colIndex) => ({
            ...commonStyles[colIndex],
            bottom: lockFooters ? 0 : undefined,
            position: locked || lockFooters ? 'sticky' : 'relative',
            zIndex: locked ? 2 : 1,
        })),
    };
}

const initialLockStyles: CellLockStyles = {
    headerStyles: [],
    dataStyles: [],
    footerStyles: [],
};

/**
 * Component used for displaying data into a table.
 * The table reformats the rows based on the screen size.
 *
 * @param param0 The table configuration.
 */
export function Table<TData>({
    tableStyle = 'data',
    data,
    rowKey,
    columns,
    sort,
    onSortChange,
    paging,
    onPageChange,
    showPageSizeDropdown,
    tableClassName,
    pagingClass,
    lockHeaders = false,
    lockFooters = false,
    height,
    maxHeight,
    isLoading = false,
}: TableProps<TData>) {
    const enabledColumns = useMemo(
        () => columns.filter(({ enabled }) => enabled ?? true),
        [columns]
    );

    useEffect(() => {
        if (enabledColumns.length === 0) {
            throw new Error('At least one column is required');
        }
        enabledColumns.reduce<string[]>((prevCols, col) => {
            if (prevCols.includes(col.id)) {
                throw new Error(`Duplicate column id: ${col.id}`);
            }
            return [...prevCols, col.id];
        }, []);
    }, [enabledColumns]);

    if ((lockHeaders || lockFooters) && height === undefined && maxHeight === undefined) {
        throw new Error(
            'Table height or maxHeight must be set if headers or footers are locked'
        );
    }

    const [lockStyles, setLockStyles] = useState<CellLockStyles>(initialLockStyles);
    const windowSize = useWindowSize();
    const headerRefs = useRefList<HTMLTableHeaderCellElement>(enabledColumns.length);

    const dataAndKeys = useMemo<DataAndKey<TData>[] | undefined>(
        () =>
            data?.map<DataAndKey<TData>>((rowData) => ({
                rowData,
                rowKey: rowKey(rowData),
            })),
        [data, rowKey]
    );

    const isCompact = isViewportAtMostMedium(windowSize);
    const primaryColumns = useMemo(() => enabledColumns.filter((col) => col.primary), [
        enabledColumns,
    ]);
    const secondaryColumns = useMemo(() => enabledColumns.filter((col) => !col.primary), [
        enabledColumns,
    ]);

    const hasFooter = useMemo(
        () => enabledColumns.some((col) => col.footer !== undefined),
        [enabledColumns]
    );

    const onResize = useDebounceCallback(200, () => {
        setLockStyles(
            getCellLockStyles(enabledColumns, headerRefs, lockHeaders, lockFooters)
        );
    });

    useEffect(() => {
        setLockStyles(
            getCellLockStyles(enabledColumns, headerRefs, lockHeaders, lockFooters)
        );
    }, [enabledColumns, headerRefs, lockFooters, lockHeaders]);

    return (
        <>
            <TableWrapper
                className={tableClassName}
                $isCompact={isCompact}
                $height={height}
                $maxHeight={maxHeight}
            >
                <SpinnerArea isLoading={isLoading} noContainer>
                    <StyledTable $isCompact={isCompact} $tableStyle={tableStyle}>
                        <TableHeader
                            tableStyle={tableStyle}
                            isCompact={isCompact}
                            columns={enabledColumns}
                            primaryColumns={primaryColumns}
                            secondaryColumns={secondaryColumns}
                            lockStyles={lockStyles.headerStyles}
                            headerRefs={headerRefs}
                            sort={sort}
                            onSortChange={onSortChange}
                            onResize={onResize}
                        />
                        <TableBody
                            tableStyle={tableStyle}
                            isCompact={isCompact}
                            columns={enabledColumns}
                            primaryColumns={primaryColumns}
                            secondaryColumns={secondaryColumns}
                            lockStyles={lockStyles.dataStyles}
                            dataAndKeys={dataAndKeys}
                        />
                        {hasFooter && (
                            <TableFooter
                                tableStyle={tableStyle}
                                isCompact={isCompact}
                                columns={enabledColumns}
                                primaryColumns={primaryColumns}
                                secondaryColumns={secondaryColumns}
                                lockStyles={lockStyles.footerStyles}
                            />
                        )}
                    </StyledTable>
                </SpinnerArea>
            </TableWrapper>

            {paging &&
                onPageChange &&
                paging.totalResults !== undefined &&
                paging.totalResults > 0 &&
                paging.totalPages !== undefined &&
                (paging.totalPages > 1 || showPageSizeDropdown) && (
                    <PagingBar
                        totalPages={paging.totalPages}
                        currentPage={paging.page}
                        pageSize={paging.pageLimit}
                        total={paging.totalResults}
                        onPageChange={onPageChange}
                        showPageSizeDropdown={showPageSizeDropdown}
                        className={pagingClass}
                    />
                )}
        </>
    );
}

const MemoizedTableComponent = memo(Table);

/**
 * Memoized version of `Table` component. Note: All props should be stable
 * or memoized in order to benefit from memoization.
 *
 * @param param0 The table configuration.
 */
export function MemoizedTable<TData>(props: TableProps<TData>) {
    return <MemoizedTableComponent {...(props as TableProps<unknown>)} />;
}
